import { Box, Grid, Typography, Paper, FormControlLabel, Checkbox } from "@material-ui/core";
import TextField from '@material-ui/core/TextField';
import { useForm, Controller } from 'react-hook-form';
//import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, ChangeEvent } from 'react';
import { StepProps } from './types';
import LocationSelect from '../../../../../utils/LocationSelect';

declare global {
  interface Window { debounceTimeout: NodeJS.Timeout | null; }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formContainer: {
      padding: theme.spacing(2)
    },
    paper: {
      padding: theme.spacing(3),
      marginBottom: theme.spacing(3)
    },
    mainTitle: {
      marginBottom: theme.spacing(3),
      fontWeight: 500
    },
    sectionTitle: {
      marginBottom: theme.spacing(2),
      fontWeight: 500
    },
    checkboxGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    checkboxRow: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(2)
    },
    textField: {
      // Adicione estilos para o TextField aqui
    }
  })
);

const schema = yup.object().shape({
  unidadeSanitariaNome: yup.string().required('Nome da unidade é obrigatório'),
  unidadeSanitariaProvincia: yup.string().required('Província é obrigatória'),
  unidadeSanitariaMunicipio: yup.string().required('Município é obrigatório'),
  unidadeSanitariaBairro: yup.string().required('Bairro é obrigatório'),
  unidadeSanitariaServico: yup.string().required('Serviço é obrigatório'),
  unidadeSanitariaRua: yup.string().required('Rua é obrigatória'),
  unidadeSanitariaEmail: yup.string().email('E-mail inválido').required('E-mail é obrigatório'),

  pacienteNome: yup.string().required('Nome do paciente é obrigatório'),
  pacienteIdade: yup.string().required('Idade é obrigatória'),
  pacienteSexo: yup.string().oneOf(['M', 'F'], 'Sexo inválido').required('Sexo é obrigatório'),
  pacienteProvincia: yup.string().required('Província do paciente é obrigatória'),
  pacienteMunicipio: yup.string().required('Município do paciente é obrigatório'),
  pacienteBairro: yup.string().required('Bairro do paciente é obrigatório'),
  pacienteEmail: yup.string().email('E-mail inválido').required('E-mail do paciente é obrigatório'),
  pacienteTelefone: yup.string().required('Telefone é obrigatório'),
  pacientePeso: yup.string().required('Peso é obrigatório'),
  pacienteAltura: yup.string().required('Altura é obrigatória'),
  pacienteGestante: yup.boolean(),
  pacienteRaca: yup.string().oneOf(['Negro', 'Branco', 'Outro'], 'Raça inválida').required('Raça é obrigatória'),
  pacienteAntecendentesMedicosSP: yup.boolean(),
  pacienteAntecendentesReaccoesAMO: yup.boolean(),
  pacienteAntecendentesCirurgicos: yup.boolean(),
  pacienteAntecendentesAbusoDependencia: yup.boolean(),
  pacienteAntecendentesAlergiasConhecidas: yup.boolean(),
});

interface FormData {
  // Dados da Unidade de Saúde
  unidadeSanitariaNome: string;
  unidadeSanitariaProvincia: string;
  unidadeSanitariaMunicipio: string;
  unidadeSanitariaBairro: string;
  unidadeSanitariaServico: string;
  unidadeSanitariaRua: string;
  unidadeSanitariaEmail: string;

  // Dados do Paciente
  pacienteNome: string;
  pacienteIdade: string;
  pacienteSexo: 'M' | 'F';
  pacienteProvincia: string;
  pacienteMunicipio: string;
  pacienteBairro: string;
  pacienteEmail: string;
  pacienteTelefone: string;
  pacientePeso: string;
  pacienteAltura: string;
  pacienteGestante: boolean;
  pacienteRaca: 'Negro' | 'Branco' | 'Outro';
  pacienteAntecendentesMedicosSP: boolean;
  pacienteAntecendentesReaccoesAMO: boolean;
  pacienteAntecendentesCirurgicos: boolean;
  pacienteAntecendentesAbusoDependencia: boolean;
  pacienteAntecendentesAlergiasConhecidas: boolean;
}

export default function DadosUnidadeSaudePaciente({ }: StepProps) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const reduxData = useSelector((state: any) => state.dadosUnidadeSaude);

  const [formData, setFormData] = useState({

    unidadeSanitariaNome: reduxData.unidadeSanitariaNome || '',
    unidadeSanitariaProvincia: reduxData.unidadeSanitariaProvincia || '',
    unidadeSanitariaMunicipio: reduxData.unidadeSanitariaMunicipio || '',
    unidadeSanitariaBairro: reduxData.unidadeSanitariaBairro || '',
    unidadeSanitariaServico: reduxData.unidadeSanitariaServico || '',
    unidadeSanitariaRua: reduxData.unidadeSanitariaRua || '',
    unidadeSanitariaEmail: reduxData.unidadeSanitariaEmail || '',

    pacienteNome: reduxData.pacienteNome || '',
    pacienteIdade: reduxData.pacienteIdade || '',
    pacienteSexo: reduxData.pacienteSexo || '',
    pacienteProvincia: reduxData.pacienteProvincia || '',
    pacienteMunicipio: reduxData.pacienteMunicipio || '',
    pacienteBairro: reduxData.pacienteBairro || '',
    pacienteEmail: reduxData.pacienteEmail || '',
    pacienteTelefone: reduxData.pacienteTelefone || '',
    pacientePeso: reduxData.pacientePeso || '',
    pacienteAltura: reduxData.pacienteAltura || '',
    pacienteGestante: reduxData.pacienteGestante || false,
    pacienteRaca: reduxData.pacienteRaca || '',
    pacienteAntecendentesMedicosSP: reduxData.pacienteAntecendentesMedicosSP || false,
    pacienteAntecendentesReaccoesAMO: reduxData.pacienteAntecendentesReaccoesAMO || false,
    pacienteAntecendentesCirurgicos: reduxData.pacienteAntecendentesCirurgicos || false,
    pacienteAntecendentesAbusoDependencia: reduxData.pacienteAntecendentesAbusoDependencia || false,
    pacienteAntecendentesAlergiasConhecidas: reduxData.pacienteAntecendentesAlergiasConhecidas || false,

  });

  // Store province IDs separately for API calls
  const [unidadeSanitariaProvinciaId, setUnidadeSanitariaProvinciaId] = useState(reduxData.unidadeSanitariaProvinciaId || '');
  const [pacienteProvinciaId, setPacienteProvinciaId] = useState(reduxData.pacienteProvinciaId || '');

  const { control, handleSubmit, formState: { errors }, setValue, trigger } = useForm({
    //resolver: yupResolver(schema),
    defaultValues: formData,
    mode: 'onChange',
  });



  useEffect(() => {
    if (window.debounceTimeout) clearTimeout(window.debounceTimeout);
    window.debounceTimeout = setTimeout(() => {
      // console.log('FormData:', formData);

      dispatch({
        type: 'UPDATE_DADOS_UNIDADE_SAUDE',
        payload: formData
      });
    }, 500);
    // eslint-disable-next-line
  }, [formData]);

  // Escutar evento de validação
  useEffect(() => {
    const handleValidateStep = async (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail?.stepIndex === 0) { // Este é o step 0
        const isValid = await trigger(); // Valida todos os campos
        
        // Enviar resultado da validação
        const resultEvent = new CustomEvent('validationResult', {
          detail: { isValid }
        });
        window.dispatchEvent(resultEvent);
      }
    };

    window.addEventListener('validateCurrentStep', handleValidateStep);
    
    return () => {
      window.removeEventListener('validateCurrentStep', handleValidateStep);
    };
  }, [trigger]);

  const handleCheckboxChange = (field: string) => (e: ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.checked
    }));
  };

  const handleExclusiveChange = (field: 'pacienteGestante' | 'pacienteRaca', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field] === value ? '' : value
    }));
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      // console.log('Campo atualizado:', e.target.name, e.target.value);
      setFormData(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    };

    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Box className={classes.formContainer}>
      <form id="dadosUnidadeSaudeForm">
        {/* Dados da Unidade de Saúde */}
        <Paper className={classes.paper} elevation={2}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Dados da Unidade de Saúde
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Controller
                name="unidadeSanitariaNome"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome da Unidade de Saúde *"
                    error={!!errors.unidadeSanitariaNome}
                    helperText={typeof errors.unidadeSanitariaNome?.message === 'string' ? errors.unidadeSanitariaNome?.message : ''} fullWidth
                    variant="outlined"
                    className={classes.textField}
                    // Atualiza tanto o RHF quanto o seu estado local
                    onChange={e => {
                      field.onChange(e); // RHF
                      setFormData(prev => ({
                        ...prev,
                        unidadeSanitariaNome: e.target.value // seu estado local
                      }));
                    }}
                    value={formData.unidadeSanitariaNome}
                  />
                )}
              />
            </Grid>

            {/* Província da Unidade de Saúde */}
            <Grid item xs={12} md={4}>
              <Controller
                name="unidadeSanitariaProvincia"
                control={control}
                render={({ field }) => (
                  <LocationSelect
                    label="Província"
                    value={field.value}
                    onChange={(value: string, item) => {
                      field.onChange(value);
                      setFormData({
                        ...formData,
                        unidadeSanitariaProvincia: value,
                        unidadeSanitariaMunicipio: ''
                      });
                      // Sincronizar com react-hook-form
                      setValue('unidadeSanitariaProvincia', value);
                      setValue('unidadeSanitariaMunicipio', '');
                      // Store the province ID for API calls
                      setUnidadeSanitariaProvinciaId(item?._id || '');
                    }}
                    required
                    error={!!errors.unidadeSanitariaProvincia}
                    helperText={typeof errors.unidadeSanitariaProvincia?.message === 'string' ? errors.unidadeSanitariaProvincia?.message : ''}
                  />
                )}
              />
            </Grid>

            {/* Município da Unidade de Saúde */}
            <Grid item xs={12} md={4}>
              <Controller
                name="unidadeSanitariaMunicipio"
                control={control}
                render={({ field }) => (
                  <LocationSelect
                    label="Município"
                    value={field.value}
                    onChange={(value: string) => {
                      field.onChange(value);
                      setFormData({ ...formData, unidadeSanitariaMunicipio: value });
                      setValue('unidadeSanitariaMunicipio', value);
                    }}
                    provinciaId={unidadeSanitariaProvinciaId}
                    disabled={!unidadeSanitariaProvinciaId}
                    required
                    error={!!errors.unidadeSanitariaMunicipio}
                    helperText={typeof errors.unidadeSanitariaMunicipio?.message === 'string' ? errors.unidadeSanitariaMunicipio?.message : ''}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="unidadeSanitariaBairro"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Bairro *"
                    error={!!errors.unidadeSanitariaBairro}
                    helperText={typeof errors.unidadeSanitariaBairro?.message === 'string' ? errors.unidadeSanitariaBairro?.message : ''}
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        unidadeSanitariaBairro: e.target.value
                      }));
                    }}
                    value={formData.unidadeSanitariaBairro}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="unidadeSanitariaRua"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Rua da Unidade"
                    error={!!errors.unidadeSanitariaRua}
                    helperText={typeof errors.unidadeSanitariaRua?.message === 'string' ? errors.unidadeSanitariaRua?.message : ''}
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        unidadeSanitariaRua: e.target.value
                      }));
                    }}
                    value={formData.unidadeSanitariaRua}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="unidadeSanitariaServico"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Serviço *"
                    error={!!errors.unidadeSanitariaServico}
                    helperText={typeof errors.unidadeSanitariaServico?.message === 'string' ? errors.unidadeSanitariaServico?.message : ''}
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        unidadeSanitariaServico: e.target.value
                      }));
                    }}
                    value={formData.unidadeSanitariaServico}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="unidadeSanitariaEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    error={!!errors.unidadeSanitariaEmail}
                    helperText={typeof errors.unidadeSanitariaEmail?.message === 'string' ? errors.unidadeSanitariaEmail?.message : ''}
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    type="email"
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        unidadeSanitariaEmail: e.target.value
                      }));
                    }}
                    value={formData.unidadeSanitariaEmail}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Dados do Paciente */}
        <Paper className={classes.paper} elevation={2}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Dados do Paciente
          </Typography>

          <Grid container spacing={2}>

            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteNome"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome e Apelido *"
                    error={!!errors.pacienteNome}
                    helperText={typeof errors.pacienteNome?.message === 'string' ? errors.pacienteNome?.message : ''}
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacienteNome: e.target.value
                      }));
                    }}
                    value={formData.pacienteNome}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteIdade"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Idade *"
                    error={!!errors.pacienteIdade}
                    helperText={typeof errors.pacienteIdade?.message === 'string' ? errors.pacienteIdade?.message : ''}
                    fullWidth
                    variant="outlined"
                    type="number"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacienteIdade: e.target.value
                      }));
                    }}
                    value={formData.pacienteIdade}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteSexo"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label="Genero *"
                    error={!!errors.pacienteSexo}
                    helperText={typeof errors.pacienteSexo?.message === 'string' ? errors.pacienteSexo?.message : ''}
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    SelectProps={{ native: true }}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacienteSexo: e.target.value
                      }));
                    }}
                    value={formData.pacienteSexo}
                  >
                    <option value=""></option>
                    <option value="M">Masculino</option>
                    <option value="F">Feminino</option>
                  </TextField>
                )}
              />
            </Grid>
            {/* Província do Paciente */}
            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteProvincia"
                control={control}
                render={({ field }) => (
                  <LocationSelect
                    label="Província do Paciente"
                    value={formData.pacienteProvincia}
                    onChange={(value: string, item) => {
                      field.onChange(value);
                      setFormData({
                        ...formData,
                        pacienteProvincia: value,
                        pacienteMunicipio: ''
                      });
                      // Sincronizar com react-hook-form
                      setValue('pacienteProvincia', value);
                      setValue('pacienteMunicipio', '');
                      // Armazenar o ID da província para chamadas da API
                      setPacienteProvinciaId(item?._id || '');
                    }}
                    required
                    error={!!errors.pacienteProvincia}
                    helperText={typeof errors.pacienteProvincia?.message === 'string' ? errors.pacienteProvincia?.message : ''}
                  />
                )}
              />
            </Grid>
            {/* Município do Paciente */}
            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteMunicipio"
                control={control}
                render={({ field }) => (
                  <LocationSelect
                    label="Município do Paciente"
                    value={formData.pacienteMunicipio}
                    onChange={(value: string) => {
                      field.onChange(value);
                      setFormData({ ...formData, pacienteMunicipio: value });
                      setValue('pacienteMunicipio', value);
                    }}
                    provinciaId={pacienteProvinciaId}
                    disabled={!pacienteProvinciaId}
                    required
                    error={!!errors.pacienteMunicipio}
                    helperText={typeof errors.pacienteMunicipio?.message === 'string' ? errors.pacienteMunicipio?.message : ''}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteEmail"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    error={!!errors.pacienteEmail}
                    helperText={typeof errors.pacienteEmail?.message === 'string' ? errors.pacienteEmail?.message : ''}
                    fullWidth
                    variant="outlined"
                    type="email"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacienteEmail: e.target.value
                      }));
                    }}
                    value={formData.pacienteEmail}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteBairro"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Bairro do Paciente"
                    error={!!errors.pacienteBairro}
                    helperText={typeof errors.pacienteBairro?.message === 'string' ? errors.pacienteBairro?.message : ''}
                    fullWidth
                    variant="outlined"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacienteBairro: e.target.value
                      }));
                    }}
                    value={formData.pacienteBairro}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="pacienteTelefone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Telefone"
                    error={!!errors.pacienteTelefone}
                    helperText={typeof errors.pacienteTelefone?.message === 'string' ? errors.pacienteTelefone?.message : ''}
                    fullWidth
                    variant="outlined"
                    type="tel"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacienteTelefone: e.target.value
                      }));
                    }}
                    value={formData.pacienteTelefone}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="pacientePeso"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Peso (kg)"
                    error={!!errors.pacientePeso}
                    helperText={typeof errors.pacientePeso?.message === 'string' ? errors.pacientePeso?.message : ''}
                    fullWidth
                    variant="outlined"
                    type="number"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacientePeso: e.target.value
                      }));
                    }}
                    value={formData.pacientePeso}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <Controller
                name="pacienteAltura"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Altura (cm)"
                    error={!!errors.pacienteAltura}
                    helperText={typeof errors.pacienteAltura?.message === 'string' ? errors.pacienteAltura?.message : ''}
                    fullWidth
                    variant="outlined"
                    type="number"
                    className={classes.textField}
                    onChange={e => {
                      field.onChange(e);
                      setFormData(prev => ({
                        ...prev,
                        pacienteAltura: e.target.value
                      }));
                    }}
                    value={formData.pacienteAltura}
                  />
                )}
              />
            </Grid>

            {/* Gestante */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Gestante:
              </Typography>
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pacienteGestante}
                      onChange={() => setFormData(prev => ({ ...prev, pacienteGestante: !prev.pacienteGestante }))}
                      color="primary"
                    />
                  }
                  label="Sim"
                />
              </div>
            </Grid>

            {/* Raça */}
            <Grid item xs={12} md={6}>
              <Controller
                name="pacienteRaca"
                control={control}
                render={({ field }) => (
                  <>
                    <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                      Raça *:
                    </Typography>
                    <div className={classes.checkboxRow}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value === 'Negro'}
                            onChange={() => {
                              field.onChange('Negro');
                              handleExclusiveChange('pacienteRaca', 'Negro');
                            }}
                            color="primary"
                          />
                        }
                        label="Negro"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value === 'Branco'}
                            onChange={() => {
                              field.onChange('Branco');
                              handleExclusiveChange('pacienteRaca', 'Branco');
                            }}
                            color="primary"
                          />
                        }
                        label="Branco"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value === 'Outro'}
                            onChange={() => {
                              field.onChange('Outro');
                              handleExclusiveChange('pacienteRaca', 'Outro');
                            }}
                            color="primary"
                          />
                        }
                        label="Outro"
                      />
                    </div>
                    {errors.pacienteRaca && (
                      <Typography variant="caption" color="error" style={{ marginTop: 4, display: 'block' }}>
                        {typeof errors.pacienteRaca?.message === 'string' ? errors.pacienteRaca?.message : ''}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid>

            {/* Antecedentes */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Antecedentes Médicos:
              </Typography>
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pacienteAntecendentesMedicosSP}
                      onChange={handleCheckboxChange('pacienteAntecendentesMedicosSP')}
                      color="primary"
                    />
                  }
                  label="Sim"
                />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Antecedentes de Reacções Adversas a Medicamentos ou Outros:
              </Typography>
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pacienteAntecendentesReaccoesAMO}
                      onChange={handleCheckboxChange('pacienteAntecendentesReaccoesAMO')}
                      color="primary"
                    />
                  }
                  label="Sim"
                />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Antecedentes Cirúrgicos:
              </Typography>
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pacienteAntecendentesCirurgicos}
                      onChange={handleCheckboxChange('pacienteAntecendentesCirurgicos')}
                      color="primary"
                    />
                  }
                  label="Sim"
                />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Antecedentes de Abuso ou Dependência:
              </Typography>
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pacienteAntecendentesAbusoDependencia}
                      onChange={handleCheckboxChange('pacienteAntecendentesAbusoDependencia')}
                      color="primary"
                    />
                  }
                  label="Sim"
                />
              </div>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                Antecedentes de Alergias Conhecidas:
              </Typography>
              <div className={classes.checkboxRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.pacienteAntecendentesAlergiasConhecidas}
                      onChange={handleCheckboxChange('pacienteAntecendentesAlergiasConhecidas')}
                      color="primary"
                    />
                  }
                  label="Sim"
                />
              </div>
            </Grid>
          </Grid>
        </Paper>
      </form>
    </Box>
  );
}
